import axios from "axios";
import tokenManager from "../utils/tokenManager";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

class ApiService {
  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      timeout: 60000,
      withCredentials: true, // Enable cookies
    });

    this.setupInterceptors();
  }

  setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      async (config) => {
        if (tokenManager.shouldRefreshToken()) {
          try {
            await tokenManager.refreshAccessToken();
          } catch (error) {
            console.warn("Pre-request token refresh failed:", error);
          }
        }
        let token = tokenManager.getAccessToken();

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          // Don't proceed if we can't get a token for protected routes
          if (!config.url.includes("/auth/")) {
            console.warn("No access token available for protected route");
          }
        }

        // Add CSRF token for state-changing requests
        if (
          ["POST", "PUT", "DELETE", "PATCH"].includes(
            config.method.toUpperCase(),
          )
        ) {
          const csrfToken = tokenManager.getCSRFToken();
          if (csrfToken) {
            config.headers["X-CSRF-Token"] = csrfToken;
          }
        }

        // Add fingerprint
        const fingerprint = await tokenManager.getFingerprint();
        if (fingerprint) {
          config.headers["X-Fingerprint"] = fingerprint;
        }

        // Set content type
        if (!(config.data instanceof FormData)) {
          config.headers["Content-Type"] = "application/json";
        }

        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response.data,
      async (error) => {
        const originalRequest = error.config;

        // Handle token expiration
        if (
          error.response?.status === 401 &&
          error.response?.data?.code === "TOKEN_EXPIRED" &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;

          try {
            const newToken = await tokenManager.refreshAccessToken();
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            if (refreshError.response?.status === 401) {
              tokenManager.clearCurrentSession();
              window.location.href = "/login?reason=session_expired";
            }

            return Promise.reject(refreshError);
          }
        }

        // Handle session timeout
        if (error.response?.data?.code === "SESSION_TIMEOUT") {
          tokenManager.clearCurrentSession();
        }

        // Handle account lockout
        if (error.response?.data?.code === "ACCOUNT_LOCKED") {
          window.dispatchEvent(
            new CustomEvent("accountLocked", {
              detail: { message: error.response.data.message },
            }),
          );
        }

        return Promise.reject(error);
      },
    );
  }

  // Auth endpoints with enhanced security
  async register(userData) {
    try {
      const response = await this.client.post("/auth/register", userData);

      if (response.success) {
        tokenManager.setTokens(
          response.accessToken,
          response.refreshToken,
          response.csrfToken,
        );

        if (response.fingerprint) {
          tokenManager.setFingerprint(response.fingerprint);
        }

        if (response.user) {
          tokenManager.setUser(response.user);
        }
      }

      return response;
    } catch (error) {
      console.error("Register API error:", error);
      throw error;
    }
  }
  // In services/api.js

  // ─── Demo Login ───────────────────────────────────────────────────────────
  // Called from the "Try demo" modal on the login page.
  // Does NOT set tokens — the caller (Login.jsx) handles that itself
  // after inspecting the response, same as the regular login flow.

  async demoLogin({ name, email, companyName }) {
    const response = await this.client.post("/auth/demo-login", {
      name,
      email,
      companyName,
    });

    if (response.success) {
      tokenManager.setTokens(
        response.accessToken,
        response.refreshToken,
        response.csrfToken,
      );

      if (response.fingerprint) {
        tokenManager.setFingerprint(response.fingerprint);
      }

      if (response.user) {
        tokenManager.setUser(response.user);
      }
    }

    return response;
  }
  // ============= Two-Factor Authentication =============

  async get2FAStatus() {
    return this.client.get("/auth/2fa/status");
  }

  async setup2FA() {
    return this.client.post("/auth/2fa/setup");
  }

  async verify2FA(token) {
    return this.client.post("/auth/2fa/verify", { token });
  }

  async disable2FA(password, token) {
    return this.client.post("/auth/2fa/disable", { password, token });
  }

  async regenerate2FABackupCodes(password, token) {
    return this.client.post("/auth/2fa/backup-codes/regenerate", {
      password,
      token,
    });
  }

  // Update login method to support 2FA
  async login(email, password, remember = false, twoFactorToken = null) {
    const fingerprint = await tokenManager.getFingerprint();

    const response = await this.client.post("/auth/login", {
      email,
      password,
      remember,
      fingerprint,
      twoFactorToken, // Add 2FA token
    });

    if (response.success) {
      tokenManager.setTokens(
        response.accessToken,
        response.refreshToken,
        response.csrfToken,
      );

      if (response.fingerprint) {
        tokenManager.setFingerprint(response.fingerprint);
      }

      if (response.user) {
        tokenManager.setUser(response.user);
      }
    }

    return response;
  }

  async logout(allDevices = false) {
    const refreshToken = tokenManager.getRefreshToken();

    try {
      await this.client.post("/auth/logout", {
        refreshToken,
        allDevices,
      });
    } finally {
      if (allDevices) {
        tokenManager.clearAll();
      } else {
        tokenManager.clearCurrentSession();
      }
    }
  }

  async getSessions() {
    return this.client.get("/auth/sessions");
  }

  async revokeSession(sessionId) {
    return this.client.delete(`/auth/sessions/${sessionId}`);
  }

  async getMe() {
    return this.client.get("/auth/me");
  }

  async forgotPassword(email) {
    return this.client.post("/auth/forgot-password", { email });
  }

  // Onboarding endpoints
  async updateCompanyDetails(companyData) {
    return this.client.put("/onboarding/company", companyData);
  }

  async inviteTeamMembers(emails) {
    return this.client.post("/onboarding/invite-team", { emails });
  }

  async uploadAsset(file) {
    const formData = new FormData();
    formData.append("file", file);
    return this.client.post("/onboarding/upload-assets", formData);
  }

  async updateWorkspaceUrl(workspaceUrl) {
    return this.client.put("/onboarding/workspace-url", { workspaceUrl });
  }

  async validateInvitation(token) {
    return this.client.get(`/auth/invitation/${token}`);
  }

  async acceptInvitation(data) {
    return this.client.post("/auth/accept-invitation", data);
  }

  // Profile management
  async updatePersonalDetails(data) {
    return this.client.put("/auth/update-profile", data);
  }

  async uploadProfileImage(formData) {
    return this.client.post("/auth/upload-profile-image", formData);
  }

  // Company endpoints
  async updateCompanyData(data) {
    return this.client.put("/company/update", data);
  }

  async uploadCompanyLogo(formData) {
    return this.client.post("/company/upload-logo", formData);
  }

  // services/api.js - Add these methods

  // Team management - UPDATED
  // Replace these methods in api.js:

  // Team management - UPDATED
  async getTeams(params = {}) {
    return this.client.get("/teams", { params });
  }

  async getTeam(teamId) {
    return this.client.get(`/teams/${teamId}`);
  }

  async createTeam(teamData) {
    return this.client.post("/teams", teamData);
  }

  async updateTeam(teamId, teamData) {
    return this.client.put(`/teams/${teamId}`, teamData);
  }

  async deleteTeam(teamId) {
    return this.client.delete(`/teams/${teamId}`);
  }

  async getUserTeams() {
    return this.client.get("/teams/my-teams");
  }

  // Team members
  async getTeamMembers(teamId) {
    return this.client.get(`/teams/${teamId}/members`);
  }
  async getTeamMembers2(teamId) {
    return this.client.get(`/teams/${teamId}/members2`);
  }
  async getTeamMembers3(teamId, params = {}) {
    return this.client.get(`/teams/${teamId}/members3`, { params });
  }
  async addTeamMember(teamId, userId, role = "member") {
    return this.client.post(`/teams/${teamId}/members`, { userId, role });
  }
  // In services/api.js - Add this method to ApiService class

  async blockTeamMemberTime(blockData) {
    return this.client.post("/appointments/block-time", blockData);
  }
  async bulkAddTeamMembers(teamId, userIds, role = "member") {
    return this.client.post(`/teams/${teamId}/members/bulk`, { userIds, role });
  }

  async inviteToTeam(teamId, email, role = "member", message = "") {
    return this.client.post(`/teams/${teamId}/invite`, {
      email,
      role,
      message,
    });
  }

  async updateTeamMember(teamId, memberId, data) {
    return this.client.put(`/teams/${teamId}/members/${memberId}`, data);
  }

  async removeTeamMember(teamId, memberId) {
    return this.client.delete(`/teams/${teamId}/members/${memberId}`);
  }

  async getTeamHierarchy(teamId) {
    return this.client.get(`/teams/${teamId}/hierarchy`);
  }

  // Company users (for adding to teams)
  async getCompanyUsers(params = {}) {
    return this.client.get("/users/company", { params }); // Changed from /team/members
  }

  async getLead(leadId) {
    return this.client.get(`/leads/${leadId}`);
  }
  async getLeads(params = {}) {
    return this.client.get("/leads", { params });
  }

  async getMyLeads(params = {}) {
    return this.client.get("/leads/my-leads", { params });
  }

  async getLeadSearchSuggestions(query, limit = 10) {
    return this.client.get("/leads/search/suggestions", {
      params: { q: query, limit },
    });
  }

  async warmupLeadCache() {
    return this.client.post("/leads/cache/warmup");
  }

  async createLead(leadData) {
    return this.client.post("/leads", leadData);
  }

  async updateLead(leadId, leadData) {
    return this.client.put(`/leads/${leadId}`, leadData);
  }

  async deleteLead(leadId) {
    return this.client.delete(`/leads/${leadId}`);
  }

  async getLeadStats() {
    return this.client.get("/leads/stats");
  }
  async getLeadsAdvanced(params = {}) {
    return this.client.get("/leads/advanced", { params });
  }

  // ============= Filter Presets =============

  async getFilterPresets(entityType = "lead") {
    return this.client.get("/leads/filter-presets", {
      params: { entityType },
    });
  }

  async getFilterPreset(presetId) {
    return this.client.get(`/leads/filter-presets/${presetId}`);
  }

  async createFilterPreset(presetData) {
    return this.client.post("/leads/filter-presets", presetData);
  }

  async updateFilterPreset(presetId, presetData) {
    return this.client.put(`/leads/filter-presets/${presetId}`, presetData);
  }

  async deleteFilterPreset(presetId) {
    return this.client.delete(`/leads/filter-presets/${presetId}`);
  }

  async recordFilterPresetUsage(presetId) {
    return this.client.post(`/leads/filter-presets/${presetId}/usage`);
  }
  // Lusha integration
  async searchLusha(filters) {
    return this.client.post("/leads/lusha/search", filters);
  }

  async importFromLusha(maxPages = 5) {
    return this.client.post("/leads/lusha/import", { maxPages });
  }

  async enrichLead(leadId, data) {
    return this.client.post(`/leads/${leadId}/enrich`, data);
  }

  async getLushaCredits() {
    return this.client.get("/leads/lusha/credits");
  }
  // services/api.js - ADD these methods to your ApiService class

  // ============= Lead Notes =============

  async getLeadNotes(leadId) {
    return this.client.get(`/leads/${leadId}/notes`);
  }

  async createLeadNote(leadId, noteData) {
    return this.client.post(`/leads/${leadId}/notes`, noteData);
  }

  async updateLeadNote(leadId, noteId, noteData) {
    return this.client.put(`/leads/${leadId}/notes/${noteId}`, noteData);
  }

  async deleteLeadNote(leadId, noteId) {
    return this.client.delete(`/leads/${leadId}/notes/${noteId}`);
  }

  // ============= Lead Attachments =============

  async getLeadAttachments(leadId) {
    return this.client.get(`/leads/${leadId}/attachments`);
  }

  async uploadLeadAttachment(leadId, formData) {
    return this.client.post(`/leads/${leadId}/attachments`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  async deleteLeadAttachment(leadId, attachmentId) {
    return this.client.delete(`/leads/${leadId}/attachments/${attachmentId}`);
  }

  // ============= Lead Activity =============

  async getLeadActivity(leadId) {
    return this.client.get(`/leads/${leadId}/activity`);
  }

  async getRelatedRecords(leadId) {
    return this.client.get(`/leads/${leadId}/related`);
  }

  // ============= Lead Actions =============

  async cloneLead(leadId) {
    return this.client.post(`/leads/${leadId}/clone`);
  }

  async markAsViewed(leadId) {
    return this.client.post(`/leads/${leadId}/view`);
  }

  // ============= Appointments =============

  async getAppointments(params = {}) {
    return this.client.get("/appointments", { params });
  }

  async getAppointment(appointmentId) {
    return this.client.get(`/appointments/${appointmentId}`);
  }

  // async createAppointment(appointmentData) {
  //   return this.client.post("/appointments", appointmentData);
  // }

  async updateAppointment(appointmentId, appointmentData) {
    return this.client.put(`/appointments/${appointmentId}`, appointmentData);
  }
  async getPlacePredictions(input) {
    return this.client.get(
      `/appointments/places/predictions?input=${encodeURIComponent(input)}`,
    );
  }
  async deleteAppointment(appointmentId) {
    return this.client.delete(`/appointments/${appointmentId}`);
  }

  async getAppointmentStats() {
    return this.client.get("/appointments/stats");
  }

  // Calendar Integrations
  async connectCalendar(provider) {
    return this.client.post("/appointments/calendars/connect", { provider });
  }

  async getCalendarIntegrations() {
    return this.client.get("/appointments/calendars/integrations");
  }

  async syncCalendar(integrationId) {
    return this.client.post(`/appointments/calendars/${integrationId}/sync`);
  }

  async disconnectCalendar(integrationId) {
    return this.client.delete(`/appointments/calendars/${integrationId}`);
  }
  // Add these methods to your ApiService class in api.js

  // ============= Tasks =============

  async getTasks(params = {}) {
    return this.client.get("/tasks", { params });
  }

  async getTask(taskId) {
    return this.client.get(`/tasks/${taskId}`);
  }

  async createTask(taskData) {
    return this.client.post("/tasks", taskData);
  }

  async updateTask(taskId, taskData) {
    return this.client.put(`/tasks/${taskId}`, taskData);
  }

  async deleteTask(taskId) {
    return this.client.delete(`/tasks/${taskId}`);
  }

  async bulkUpdateTasks(taskIds, updates) {
    return this.client.post("/tasks/bulk-update", { taskIds, updates });
  }

  async getTaskStats() {
    return this.client.get("/tasks/stats");
  }

  async getTeamTaskLoad() {
    return this.client.get("/tasks/team-load");
  }

  async addTaskComment(taskId, content, attachments = []) {
    return this.client.post(`/tasks/${taskId}/comments`, {
      content,
      attachments,
    });
  }

  // ============= Tickets =============

  async getTickets(params = {}) {
    return this.client.get("/tickets", { params });
  }

  async getTicket(ticketId) {
    return this.client.get(`/tickets/${ticketId}`);
  }

  async createTicket(ticketData) {
    return this.client.post("/tickets", ticketData);
  }

  async updateTicket(ticketId, ticketData) {
    return this.client.put(`/tickets/${ticketId}`, ticketData);
  }

  async deleteTicket(ticketId) {
    return this.client.delete(`/tickets/${ticketId}`);
  }

  async bulkUpdateTickets(ticketIds, updates) {
    return this.client.post("/tickets/bulk-update", { ticketIds, updates });
  }

  async getTicketStats() {
    return this.client.get("/tickets/stats");
  }

  async getTicketsByStatus() {
    return this.client.get("/tickets/by-status");
  }

  async addTicketComment(
    ticketId,
    content,
    isInternal = false,
    attachments = [],
  ) {
    return this.client.post(`/tickets/${ticketId}/comments`, {
      content,
      isInternal,
      attachments,
    });
  }

  // services/api.js - Add these methods to your ApiService class

  // ============= HubSpot Integration =============

  async getHubSpotStatus() {
    return this.client.get("/hubspot/integration/status");
  }

  async getHubSpotAuthUrl() {
    return this.client.get("/hubspot/oauth/url");
  }

  async completeHubSpotOAuth(code) {
    return this.client.post("/hubspot/oauth/callback", { code });
  }

  async disconnectHubSpot() {
    return this.client.delete("/hubspot/integration");
  }

  async updateHubSpotSettings(settings) {
    return this.client.put("/hubspot/integration/settings", settings);
  }

  async syncHubSpot(syncType = "full") {
    return this.client.post("/hubspot/sync", { syncType });
  }

  async getHubSpotSyncLogs(params = {}) {
    return this.client.get("/hubspot/sync/logs", { params });
  }

  async getHubSpotSyncLog(logId) {
    return this.client.get(`/hubspot/sync/logs/${logId}`);
  }

  async retryHubSpotSync(logId) {
    return this.client.post(`/hubspot/sync/${logId}/retry`);
  }

  async getHubSpotSyncStatus() {
    return this.client.get("/hubspot/sync/status");
  }

  async cancelHubSpotSync() {
    return this.client.post("/hubspot/sync/cancel");
  }

  async testHubSpotConnection() {
    return this.client.get("/hubspot/integration/test");
  }

  async getHubSpotSyncHistory(params = {}) {
    return this.client.get("/hubspot/sync/history", { params });
  }

  // ============= Opportunities =============

  // In api.js - UPDATE these methods

  async getOpportunities(params = {}) {
    return this.client.get("/opportunities", { params });
  }

  async getOpportunitiesAdvanced(params = {}) {
    return this.client.get("/opportunities/advanced", { params });
  }

  async getMyOpportunities(params = {}) {
    return this.client.get("/opportunities/me", { params });
  }

  async getOpportunity(opportunityId) {
    return this.client.get(`/opportunities/${opportunityId}`);
  }
  async getOpportunityAppointments(opportunityId) {
    return this.client.get(`/opportunities/${opportunityId}/appointments`);
  }

  async createOpportunity(opportunityData) {
    return this.client.post("/opportunities", opportunityData);
  }

  async updateOpportunity(opportunityId, opportunityData) {
    return this.client.put(`/opportunities/${opportunityId}`, opportunityData);
  }

  async deleteOpportunity(opportunityId) {
    return this.client.delete(`/opportunities/${opportunityId}`);
  }

  async getOpportunityStats() {
    return this.client.get("/opportunities/stats");
  }

  async getOpportunityPipeline() {
    return this.client.get("/opportunities/pipeline");
  }

  async updateOpportunityStage(opportunityId, stage) {
    return this.client.put(`/opportunities/${opportunityId}/stage`, { stage });
  }

  async addOpportunityProduct(opportunityId, productData) {
    return this.client.post(
      `/opportunities/${opportunityId}/products`,
      productData,
    );
  }

  async updateOpportunityProduct(opportunityId, productId, productData) {
    return this.client.put(
      `/opportunities/${opportunityId}/products/${productId}`,
      productData,
    );
  }

  async deleteOpportunityProduct(opportunityId, productId) {
    return this.client.delete(
      `/opportunities/${opportunityId}/products/${productId}`,
    );
  }
  async getSalesforceStatus() {
    return this.client.get("/salesforce/status");
  }

  async getSalesforceAuthUrl() {
    return this.client.get("/salesforce/oauth/url");
  }

  async completeSalesforceOAuth(code) {
    return this.client.post("/salesforce/oauth/callback", { code });
  }

  async disconnectSalesforce() {
    return this.client.delete("/salesforce");
  }

  async updateSalesforceSettings(settings) {
    return this.client.put("/salesforce/settings", settings);
  }

  async syncSalesforce() {
    return this.client.post("/salesforce/sync");
  }

  async getSalesforceSyncLogs(params = {}) {
    return this.client.get("/salesforce/sync/logs", { params });
  }
  // Add to api.js

  // ============= Case Feed Items =============

  async getCaseFeedItems(caseId, params = {}) {
    return this.client.get(`/cases/${caseId}/feed-items`, { params });
  }
  // async testSalesforceConnection() {
  //   return this.client.get("/salesforce/integration/test");
  // }

  async getZohoStatus() {
    return this.client.get("/zoho/status");
  }

  async getZohoAuthUrl() {
    return this.client.get("/zoho/auth-url");
  }

  async disconnectZoho() {
    return this.client.delete("/zoho/disconnect");
  }

  async syncZoho() {
    return this.client.post("/zoho/sync");
  }

  async updateZohoConfig(settings) {
    return this.client.put("/zoho/config", settings);
  }

  async importLeads(formData) {
    return this.client.post("/leads/import", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  async downloadLeadTemplate(format = "csv") {
    const response = await this.client.get(`/leads/import/template`, {
      params: { format },
      responseType: "blob",
    });
    return response;
  }

  async getTodaysLeads() {
    return this.client.get("/leads/today");
  }

  async assignLead(leadId, assignedTo) {
    return this.client.post(`/leads/${leadId}/assign`, { assignedTo });
  }

  async getLeadPotStats() {
    return this.client.get("/leads/pot-stats");
  }

  async autoAssignLeads(maxLeadsPerUser = 10) {
    return this.client.post("/leads/auto-assign", { maxLeadsPerUser });
  }

  async bulkAssignLeads(leadIds, assignedTo) {
    return this.client.post("/leads/bulk-assign", { leadIds, assignedTo });
  }

  async qualifyLead(leadId, data) {
    return this.client.post(`/leads/${leadId}/qualify`, data);
  }

  async disqualifyLead(leadId, reason) {
    return this.client.post(`/leads/${leadId}/disqualify`, { reason });
  }
  async convertLead(leadId, conversionData) {
    return this.client.post(
      `/leads/${leadId}/convert-to-opportunity`,
      conversionData,
    );
  }
  async convertLeadToOpportunity(leadId, opportunityData) {
    return this.client.post(
      `/leads/${leadId}/convert-to-opportunity`,
      opportunityData,
    );
  }

  async createAppointmentFromLead(appointmentData) {
    return this.client.post("/appointments/from-lead", appointmentData);
  }
  async createAppointmentFromOpportunity(appointmentData) {
    return this.client.post("/appointments/from-opportunity", appointmentData);
  }
  /**
   * Get call system integration status
   */
  async getCallSystemIntegration() {
    return this.client.get("/call-integration/integration");
  }
  // In services/api.js - ADD THESE METHODS

  /**
   * Sync appointment-booked calls from call system
   * Fetches calls where appointmentBooked=true and matches to entities
   * @param {number} days - Number of days to look back (default: 30)
   */
  async syncAppointmentBookedCalls(days = 30) {
    return this.client.post(
      "/appointment-call-sync/sync-booked",
      {},
      {
        params: { days },
      },
    );
  }

  /**
   * Get appointment-booked call sync status
   */
  async getAppointmentBookedSyncStatus() {
    return this.client.get("/appointment-call-sync/sync-booked/status");
  }

  /**
   * Connect to call system (initiates OAuth flow)
   */
  async connectCallSystem(data) {
    return this.client.post("/call-integration/connect", data);
  }
  async getFeedItems(params = {}) {
    return this.client.get("/feed-items", { params });
  }

  async getFeedItem(feedItemId) {
    return this.client.get(`/feed-items/${feedItemId}`);
  }
  async getCaseTasks(caseId, params = {}) {
    return this.client.get(`/cases/${caseId}/tasks`, { params });
  }
  async getTasksByAccount(accountId) {
    return this.client.get(`/accounts/${accountId}/tasks`);
  }
  async getFeedItemComments(feedItemId) {
    return this.client.get(`/feed-items/${feedItemId}/comments`);
  }

  async getFeedItemAttachments(feedItemId) {
    return this.client.get(`/feed-items/${feedItemId}/attachments`);
  }

  async getFeedItemsByParent(parentType, parentId) {
    return this.client.get(`/feed-items/parent/${parentType}/${parentId}`);
  }

  async getFeedItemStats() {
    return this.client.get("/feed-items/stats");
  }
  /**
   * Disconnect call system integration
   */
  async disconnectCallSystem() {
    return this.client.post("/call-integration/disconnect");
  }

  /**
   * Get sync status (historical sync)
   */
  async getCallSystemSyncStatus() {
    return this.client.get("/call-integration/sync/status");
  }

  /**
   * Trigger historical sync (all calls in date range)
   */
  async triggerCallSystemSync(options = {}) {
    return this.client.post("/call-integration/sync/initial", options);
  }

  /**
   * Sync appointment calls (efficient, 97% more efficient than full sync)
   * @param {number} days - Number of days to look back (default: 2)
   */
  async syncAppointmentCalls(days = 2) {
    return this.client.post("/appointment-call-sync/sync", undefined, {
      params: { days },
    });
  }

  /**
   * Sync ALL appointment calls (fetches from call system)
   * @param {number} days - Number of days to look back (default: 5)
   */
  async syncAppointmentAllCalls(days = 5) {
    return this.client.post("/appointment-call-sync/sync-all", undefined, {
      params: { days },
    });
  }

  /**
   * Sync calls for a specific appointment
   * @param {string} appointmentId - UUID of the appointment
   */
  async syncSingleAppointmentCalls(appointmentId) {
    return this.client.post(`/appointment-call-sync/sync/${appointmentId}`);
  }

  /**
   * Get appointment sync statistics
   */
  async getAppointmentSyncStats() {
    return this.client.get("/appointment-call-sync/stats");
  }

  /**
   * Get calls for a specific lead
   * @param {string} leadId - UUID of the lead
   */
  async getLeadCalls(leadId) {
    return this.client.get(`/call-integration/leads/${leadId}/calls`);
  }

  /**
   * Get calls for a specific appointment
   * @param {string} appointmentId - UUID of the appointment
   */
  async getAppointmentCalls(appointmentId) {
    return this.client.get(
      `/call-integration/appointments/${appointmentId}/calls`,
    );
  }

  /**
   * Lookup entity by phone number
   * @param {string} phoneNumber - Phone number to lookup
   */
  async lookupByPhone(phoneNumber) {
    return this.client.get(
      `/call-integration/lookup/${encodeURIComponent(phoneNumber)}`,
    );
  }

  /**
   * Test call integration connection
   */
  async testCallIntegration() {
    return this.client.get("/call-integration/test");
  }

  // Account Files
  async getAccountFiles(accountId) {
    return this.client.get(`/accounts/${accountId}/files`);
  }
  async uploadAccountFile(accountId, formData) {
    return this.client.post(`/accounts/${accountId}/files`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }
  async deleteAccountFile(accountId, fileId) {
    return this.client.delete(`/accounts/${accountId}/files/${fileId}`);
  }

  // All Files
  async getAllFiles(params) {
    return this.client.get("/files", { params });
  }
  async uploadFile(formData) {
    return this.client.post("/files", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }
  async deleteFile(fileId) {
    return this.client.delete(`/files/${fileId}`);
  }
  /**
   * Get appointment sync statistics
   */
  async getAppointmentSyncStats() {
    return this.client.get("/appointment-call-sync/stats");
  }
  async getLeadCalls(leadId) {
    return this.client.get(`/call-integration/leads/${leadId}/calls`);
  }

  async triggerCallSystemSync(options = {}) {
    return this.client.post("/call-integration/sync/initial", options);
  }

  async getCallSystemSyncStatus() {
    return this.client.get("/call-integration/sync/status");
  }

  // In your existing api.js, UPDATE these methods:

  // ============= Appointments =============

  // async getAppointments(params = {}) {
  //   // params can include: scope ('personal', 'team', 'all'), teamId, etc.
  //   return this.client.get("/appointments", { params });
  // }

  // async getAppointment(appointmentId) {
  //   return this.client.get(`/appointments/${appointmentId}`);
  // }

  async createAppointment(appointmentData) {
    // appointmentData MUST include assignedTo
    return this.client.post("/appointments", appointmentData);
  }

  // async updateAppointment(appointmentId, appointmentData) {
  //   return this.client.put(`/appointments/${appointmentId}`, appointmentData);
  // }

  // async deleteAppointment(appointmentId) {
  //   return this.client.delete(`/appointments/${appointmentId}`);
  // }

  // async getAppointmentStats() {
  //   return this.client.get("/appointments/stats");
  // }

  // Team Calendar Methods
  async getTeamCalendarSettings(teamId) {
    return this.client.get(`/appointments/teams/${teamId}/calendar/settings`);
  }

  async updateTeamCalendarSettings(teamId, settings) {
    return this.client.put(
      `/appointments/teams/${teamId}/calendar/settings`,
      settings,
    );
  }

  async assignTeamMemberColor(teamId, userId, color) {
    return this.client.put(
      `/appointments/teams/${teamId}/calendar/members/${userId}/color`,
      { color },
    );
  }

  async getTeamAppointments(teamId, params = {}) {
    // params: startDate, endDate, memberIds, status, type, includePrivate
    return this.client.get(
      `/appointments/teams/${teamId}/calendar/appointments`,
      { params },
    );
  }

  async createInstallAppointment(appointmentData) {
    return this.client.post("/appointments/from-account", appointmentData);
  }
  async getEngineeringTeamMembers() {
    return this.client.get("/teams/engineering/members");
  }
  async syncTeamCalendar(teamId) {
    return this.client.post(`/appointments/teams/${teamId}/calendar/sync`);
  }

  async getTeamCalendarMembers(teamId) {
    return this.client.get(`/appointments/teams/${teamId}/calendar/members`);
  }

  // Personal appointments helper
  async getMyAppointments(params = {}) {
    return this.client.get("/appointments", {
      params: { ...params, scope: "personal" },
    });
  }
  async getUserTeamContext() {
    return this.client.get("/teams/my-context");
  }
  async checkAppointmentViability(appointmentData) {
    return this.client.post("/appointments/check-viability", appointmentData);
  }

  async geocodeAddress(address) {
    return this.client.post("/appointments/geocode", { address });
  }

  async getPlaceDetails(placeId) {
    return this.client.get(
      `/appointments/places/details?placeId=${encodeURIComponent(placeId)}`,
    );
  }

  async suggestNearbySlots(params) {
    // params: { placeId, lat, lng, address, startDate, endDate, assignedTo, durationMinutes, maxResults }
    return this.client.post("/appointments/suggest-nearby", params);
  }

  // services/api.js - ADD these methods to ApiService class

  // ============= Accounts =============

  async getAccounts(params = {}) {
    return this.client.get("/accounts", { params });
  }

  async getAccount(accountId) {
    return this.client.get(`/accounts/${accountId}`);
  }

  async createAccount(accountData) {
    return this.client.post("/accounts", accountData);
  }

  async updateAccount(accountId, accountData) {
    return this.client.put(`/accounts/${accountId}`, accountData);
  }

  async deleteAccount(accountId) {
    return this.client.delete(`/accounts/${accountId}`);
  }

  async getAccountStats() {
    return this.client.get("/accounts/stats");
  }

  async getAccountHierarchy(accountId) {
    return this.client.get(`/accounts/${accountId}/hierarchy`);
  }

  // ============= Contacts =============

  async getContacts(params = {}) {
    return this.client.get("/contacts", { params });
  }

  async getContact(contactId) {
    return this.client.get(`/contacts/${contactId}`);
  }

  async createContact(contactData) {
    return this.client.post("/contacts", contactData);
  }

  async updateContact(contactId, contactData) {
    return this.client.put(`/contacts/${contactId}`, contactData);
  }

  async deleteContact(contactId) {
    return this.client.delete(`/contacts/${contactId}`);
  }

  async getContactStats() {
    return this.client.get("/contacts/stats");
  }

  async getContactHierarchy(contactId) {
    return this.client.get(`/contacts/${contactId}/hierarchy`);
  }

  // In services/api.js - Add these Account detail methods

  // ============= Account Notes =============

  async getAccountNotes(accountId) {
    return this.client.get(`/accounts/${accountId}/notes`);
  }

  async createAccountNote(accountId, noteData) {
    return this.client.post(`/accounts/${accountId}/notes`, noteData);
  }

  async updateAccountNote(accountId, noteId, noteData) {
    return this.client.put(`/accounts/${accountId}/notes/${noteId}`, noteData);
  }

  async deleteAccountNote(accountId, noteId) {
    return this.client.delete(`/accounts/${accountId}/notes/${noteId}`);
  }

  // ============= Account Attachments =============

  async getAccountAttachments(accountId) {
    return this.client.get(`/accounts/${accountId}/attachments`);
  }

  async uploadAccountAttachment(accountId, formData) {
    return this.client.post(`/accounts/${accountId}/attachments`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  async deleteAccountAttachment(accountId, attachmentId) {
    return this.client.delete(
      `/accounts/${accountId}/attachments/${attachmentId}`,
    );
  }

  // ============= Account Activity =============

  async getAccountActivity(accountId) {
    return this.client.get(`/accounts/${accountId}/activity`);
  }
  // services/api.js - ADD these methods

  // ============= Opportunity Notes =============

  async getOpportunityNotes(opportunityId) {
    return this.client.get(`/opportunities/${opportunityId}/notes`);
  }

  async createOpportunityNote(opportunityId, noteData) {
    return this.client.post(`/opportunities/${opportunityId}/notes`, noteData);
  }

  async updateOpportunityNote(opportunityId, noteId, noteData) {
    return this.client.put(
      `/opportunities/${opportunityId}/notes/${noteId}`,
      noteData,
    );
  }

  async deleteOpportunityNote(opportunityId, noteId) {
    return this.client.delete(
      `/opportunities/${opportunityId}/notes/${noteId}`,
    );
  }

  // ============= Opportunity Attachments =============

  async getOpportunityAttachments(opportunityId) {
    return this.client.get(`/opportunities/${opportunityId}/attachments`);
  }

  async uploadOpportunityAttachment(opportunityId, formData) {
    return this.client.post(
      `/opportunities/${opportunityId}/attachments`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
  }

  async deleteOpportunityAttachment(opportunityId, attachmentId) {
    return this.client.delete(
      `/opportunities/${opportunityId}/attachments/${attachmentId}`,
    );
  }

  // ============= Opportunity Activity =============

  async getOpportunityActivity(opportunityId) {
    return this.client.get(`/opportunities/${opportunityId}/activity`);
  }
  async getProjects(params = {}) {
    return this.client.get("/projects", { params });
  }
  async getProjectsAdvanced(params = {}) {
    return this.client.get("/projects/advanced", { params });
  }

  async getProject(projectId) {
    return this.client.get(`/projects/${projectId}`);
  }

  async createProject(projectData) {
    return this.client.post("/projects", projectData);
  }

  async updateProject(projectId, projectData) {
    return this.client.put(`/projects/${projectId}`, projectData);
  }

  async deleteProject(projectId) {
    return this.client.delete(`/projects/${projectId}`);
  }

  async getProjectsByAccount(accountId) {
    return this.client.get(`/projects`, { params: { accountId } });
  }

  async getProjectStats() {
    return this.client.get("/projects/stats");
  }

  // Project Notes
  async getProjectNotes(projectId) {
    return this.client.get(`/projects/${projectId}/notes`);
  }

  async createProjectNote(projectId, noteData) {
    return this.client.post(`/projects/${projectId}/notes`, noteData);
  }

  async updateProjectNote(projectId, noteId, noteData) {
    return this.client.put(`/projects/${projectId}/notes/${noteId}`, noteData);
  }

  async deleteProjectNote(projectId, noteId) {
    return this.client.delete(`/projects/${projectId}/notes/${noteId}`);
  }

  // Project Attachments
  async getProjectAttachments(projectId) {
    return this.client.get(`/projects/${projectId}/attachments`);
  }

  async uploadProjectAttachment(projectId, formData) {
    return this.client.post(`/projects/${projectId}/attachments`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  async deleteProjectAttachment(projectId, attachmentId) {
    return this.client.delete(
      `/projects/${projectId}/attachments/${attachmentId}`,
    );
  }

  // Project Activity
  async getProjectActivity(projectId) {
    return this.client.get(`/projects/${projectId}/activity`);
  }

  // ============= Cases =============

  // In services/api.js - Add these Case methods to your ApiService class

  // ============= Cases =============

  async getCases(params = {}) {
    return this.client.get("/cases", { params });
  }

  async getCase(caseId) {
    return this.client.get(`/cases/${caseId}`);
  }

  async createCase(caseData) {
    return this.client.post("/cases", caseData);
  }

  async updateCase(caseId, caseData) {
    return this.client.put(`/cases/${caseId}`, caseData);
  }

  async deleteCase(caseId) {
    return this.client.delete(`/cases/${caseId}`);
  }

  async getCasesByAccount(accountId) {
    return this.client.get(`/cases/account/${accountId}`);
  }

  async getCasesByContact(contactId) {
    return this.client.get(`/cases/contact/${contactId}`);
  }

  // ============= Case Notes =============

  async getCaseNotes(caseId) {
    return this.client.get(`/cases/${caseId}/notes`);
  }

  async createCaseNote(caseId, noteData) {
    return this.client.post(`/cases/${caseId}/notes`, noteData);
  }

  async updateCaseNote(caseId, noteId, noteData) {
    return this.client.put(`/cases/${caseId}/notes/${noteId}`, noteData);
  }

  async deleteCaseNote(caseId, noteId) {
    return this.client.delete(`/cases/${caseId}/notes/${noteId}`);
  }

  // ============= Case Attachments =============

  async getCaseAttachments(caseId) {
    return this.client.get(`/cases/${caseId}/attachments`);
  }

  async uploadCaseAttachment(caseId, formData) {
    return this.client.post(`/cases/${caseId}/attachments`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  async deleteCaseAttachment(caseId, attachmentId) {
    return this.client.delete(`/cases/${caseId}/attachments/${attachmentId}`);
  }

  // ============= Case Activity =============

  async getCaseActivity(caseId) {
    return this.client.get(`/cases/${caseId}/activity`);
  }

  // ============= Visits =============

  async getVisits(params = {}) {
    return this.client.get("/visits", { params });
  }

  async getVisit(visitId) {
    return this.client.get(`/visits/${visitId}`);
  }

  async createVisit(visitData) {
    return this.client.post("/visits", visitData);
  }

  async updateVisit(visitId, visitData) {
    return this.client.put(`/visits/${visitId}`, visitData);
  }

  async deleteVisit(visitId) {
    return this.client.delete(`/visits/${visitId}`);
  }

  async getVisitsByAccount(accountId) {
    return this.client.get(`/visits`, { params: { accountId } });
  }

  // ============= Broadband Information =============

  async getBroadbandInfos(params = {}) {
    return this.client.get("/broadband-information", { params });
  }

  async getBroadbandInfo(broadbandInfoId) {
    return this.client.get(`/broadband-information/${broadbandInfoId}`);
  }

  async createBroadbandInfo(broadbandInfoData) {
    return this.client.post("/broadband-information", broadbandInfoData);
  }

  async updateBroadbandInfo(broadbandInfoId, broadbandInfoData) {
    return this.client.put(
      `/broadband-information/${broadbandInfoId}`,
      broadbandInfoData,
    );
  }

  async deleteBroadbandInfo(broadbandInfoId) {
    return this.client.delete(`/broadband-information/${broadbandInfoId}`);
  }

  async getBroadbandInfosByAccount(accountId) {
    return this.client.get(`/broadband-information`, { params: { accountId } });
  }

  // ============= Line Information =============

  async getLineInfos(params = {}) {
    return this.client.get("/line-information", { params });
  }

  async getLineInfo(lineInfoId) {
    return this.client.get(`/line-information/${lineInfoId}`);
  }

  async createLineInfo(lineInfoData) {
    return this.client.post("/line-information", lineInfoData);
  }

  async updateLineInfo(lineInfoId, lineInfoData) {
    return this.client.put(`/line-information/${lineInfoId}`, lineInfoData);
  }

  async deleteLineInfo(lineInfoId) {
    return this.client.delete(`/line-information/${lineInfoId}`);
  }

  async getLineInfosByAccount(accountId) {
    return this.client.get(`/line-information/account/${accountId}`);
  }

  // ============= Assets =============

  // Add these methods to your existing api.js ApiService class

  // ============= Products =============

  async getProducts(params = {}) {
    return this.client.get("/products", { params });
  }

  async getProduct(productId) {
    return this.client.get(`/products/${productId}`);
  }

  async createProduct(productData) {
    return this.client.post("/products", productData);
  }

  async updateProduct(productId, productData) {
    return this.client.put(`/products/${productId}`, productData);
  }

  async deleteProduct(productId) {
    return this.client.delete(`/products/${productId}`);
  }

  async getProductStats() {
    return this.client.get("/products/stats");
  }

  // ============= Assets =============

  async getAssets(params = {}) {
    return this.client.get("/assets", { params });
  }

  async getAsset(assetId) {
    return this.client.get(`/assets/${assetId}`);
  }

  async createAsset(assetData) {
    return this.client.post("/assets", assetData);
  }

  async updateAsset(assetId, assetData) {
    return this.client.put(`/assets/${assetId}`, assetData);
  }

  async deleteAsset(assetId) {
    return this.client.delete(`/assets/${assetId}`);
  }

  async getAssetsByAccount(accountId) {
    return this.client.get(`/assets/account/${accountId}`);
  }

  // ============= Account Projects (if not already exists) =============

  async getAccountProjects(accountId) {
    return this.client.get(`/accounts/${accountId}/projects`);
  }
  // ============= Users (for assignment dropdowns) =============

  async getUsers(params = {}) {
    return this.client.get("/users", { params });
  }

  async getUser(userId) {
    return this.client.get(`/users/${userId}`);
  }

  // services/api.js - ADD these methods

  // ============= Project Templates =============

  async getProjectTemplates(params = {}) {
    return this.client.get("/project-templates", { params });
  }

  async getProjectTemplate(templateId) {
    return this.client.get(`/project-templates/${templateId}`);
  }

  async createProjectTemplate(templateData) {
    return this.client.post("/project-templates", templateData);
  }

  async updateProjectTemplate(templateId, templateData) {
    return this.client.put(`/project-templates/${templateId}`, templateData);
  }

  async deleteProjectTemplate(templateId) {
    return this.client.delete(`/project-templates/${templateId}`);
  }

  // Task Templates
  async getTaskTemplates(projectTemplateId) {
    return this.client.get(`/project-templates/${projectTemplateId}/tasks`);
  }

  async createTaskTemplate(projectTemplateId, taskTemplateData) {
    return this.client.post(
      `/project-templates/${projectTemplateId}/tasks`,
      taskTemplateData,
    );
  }

  async updateTaskTemplate(
    projectTemplateId,
    taskTemplateId,
    taskTemplateData,
  ) {
    return this.client.put(
      `/project-templates/${projectTemplateId}/tasks/${taskTemplateId}`,
      taskTemplateData,
    );
  }

  async deleteTaskTemplate(projectTemplateId, taskTemplateId) {
    return this.client.delete(
      `/project-templates/${projectTemplateId}/tasks/${taskTemplateId}`,
    );
  }

  // Create project with selected task templates
  async createProjectWithTasks(projectData) {
    return this.client.post("/projects/with-tasks", projectData);
  }
  // Add these methods to your ApiService class in api.js

  // ============= Project Tasks =============

  async getProjectTasks(params = {}) {
    return this.client.get("/project-tasks", { params });
  }

  async getProjectTask(taskId) {
    return this.client.get(`/project-tasks/${taskId}`);
  }

  async createProjectTask(taskData) {
    return this.client.post("/project-tasks", taskData);
  }

  async updateProjectTask(taskId, taskData) {
    return this.client.put(`/project-tasks/${taskId}`, taskData);
  }

  async deleteProjectTask(taskId) {
    return this.client.delete(`/project-tasks/${taskId}`);
  }

  // Project Task Notes
  async getProjectTaskNotes(taskId) {
    return this.client.get(`/project-tasks/${taskId}/notes`);
  }

  async createProjectTaskNote(taskId, noteData) {
    return this.client.post(`/project-tasks/${taskId}/notes`, noteData);
  }

  async updateProjectTaskNote(taskId, noteId, noteData) {
    return this.client.put(
      `/project-tasks/${taskId}/notes/${noteId}`,
      noteData,
    );
  }

  async deleteProjectTaskNote(taskId, noteId) {
    return this.client.delete(`/project-tasks/${taskId}/notes/${noteId}`);
  }

  // Project Task Attachments
  async getProjectTaskAttachments(taskId) {
    return this.client.get(`/project-tasks/${taskId}/attachments`);
  }

  async uploadProjectTaskAttachment(taskId, formData) {
    return this.client.post(`/project-tasks/${taskId}/attachments`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  async deleteProjectTaskAttachment(taskId, attachmentId) {
    return this.client.delete(
      `/project-tasks/${taskId}/attachments/${attachmentId}`,
    );
  }

  // Project Task Comments
  async getProjectTaskComments(taskId) {
    return this.client.get(`/project-tasks/${taskId}/comments`);
  }

  async createProjectTaskComment(taskId, commentData) {
    return this.client.post(`/project-tasks/${taskId}/comments`, commentData);
  }

  // Project Task Activity
  async getProjectTaskActivity(taskId) {
    return this.client.get(`/project-tasks/${taskId}/activity`);
  }
  async search(query, options = {}) {
    const { signal, entities, limit, offset, ...queryOptions } = options;

    const params = new URLSearchParams({
      q: query,
    });

    // Only add defined options to params
    if (entities) params.append("entities", entities.join(","));
    if (limit) params.append("limit", limit);
    if (offset) params.append("offset", offset);

    // Pass signal to axios config, not as a query param
    return this.client.get(`/search/global?${params}`, { signal });
  }

  async advancedSearch(filters) {
    return this.client.post("/search/advanced", filters);
  }

  async clearSearchCache() {
    return this.client.delete("/search/cache");
  }
  // Add these methods to the ApiService class in api.js (if not already present)

  // async getSalesforceStatus() {
  //   return this.client.get("/salesforce/status");
  // }

  // async getSalesforceAuthUrl() {
  //   return this.client.get("/salesforce/auth");
  // }

  // async disconnectSalesforce() {
  //   return this.client.delete("/salesforce/disconnect");
  // }

  async testSalesforceConnection() {
    return this.client.get("/salesforce/test-connection");
  }

  async getSalesforceSampleLead() {
    return this.client.get("/salesforce/test/sample-lead");
  }

  async analyzeSalesforceLeads(limit = 10) {
    return this.client.get("/salesforce/test/analyze-leads", {
      params: { limit },
    });
  }

  async testSalesforceSync(options = {}) {
    return this.client.post("/salesforce/sync/test", options);
  }

  // ============= Core Data =============

  async syncSalesforceLeads(options = {}) {
    return this.client.post("/salesforce/sync/leads", options);
  }
  // In api.js, add these methods:
  async syncSalesforceEmailTemplates(options = {}) {
    return this.client.post("/salesforce/sync/emails", options);
  }
  // ============= Feed Items Sync =============

  async syncSalesforceFeedItems(options = {}) {
    return this.client.post("/salesforce/sync/feed-items", options);
  }
  async syncSalesforceContentNotes(options = {}) {
    return this.client.post("/salesforce/sync/content-notes", options);
  }

  async syncSalesforceCaseFeed(options = {}) {
    return this.client.post("/salesforce/sync/case-feed", options);
  }

  async syncSalesforceAccountFeed(options = {}) {
    return this.client.post("/salesforce/sync/account-feed", options);
  }

  async syncSalesforceOpportunityFeed(options = {}) {
    return this.client.post("/salesforce/sync/opportunity-feed", options);
  }

  async syncSalesforceLeadFeed(options = {}) {
    return this.client.post("/salesforce/sync/lead-feed", options);
  }

  async syncSalesforceProjectFeed(options = {}) {
    return this.client.post("/salesforce/sync/project-feed", options);
  }

  async syncSalesforceContactFeed(options = {}) {
    return this.client.post("/salesforce/sync/contact-feed", options);
  }
  async syncSalesforceUsers(options = {}) {
    return this.client.post("/salesforce/sync/users", options);
  }

  async syncSalesforceAccounts(options = {}) {
    return this.client.post("/salesforce/sync/accounts", options);
  }

  async syncSalesforceContacts(options = {}) {
    return this.client.post("/salesforce/sync/contacts", options);
  }

  async syncSalesforceOpportunities(options = {}) {
    return this.client.post("/salesforce/sync/opportunities", options);
  }

  // ============= Products & Assets =============

  async syncSalesforceProducts(options = {}) {
    return this.client.post("/salesforce/sync/products", options);
  }

  async syncSalesforceAssets(options = {}) {
    return this.client.post("/salesforce/sync/assets", options);
  }

  // ============= Projects =============

  async syncSalesforceProjectTemplates(options = {}) {
    return this.client.post("/salesforce/sync/project-templates", options);
  }

  async syncSalesforceTaskTemplates(options = {}) {
    return this.client.post("/salesforce/sync/task-templates", options);
  }

  async syncSalesforceProjects(options = {}) {
    return this.client.post("/salesforce/sync/projects", options);
  }

  async syncSalesforceProjectTasks(options = {}) {
    return this.client.post("/salesforce/sync/project-tasks", options);
  }

  // ============= Telecommunications & Infrastructure =============

  async syncSalesforceVisits(options = {}) {
    return this.client.post("/salesforce/sync/visits", options);
  }

  async syncSalesforceBroadbandInformation(options = {}) {
    return this.client.post("/salesforce/sync/broadband-information", options);
  }

  async syncSalesforceLineInformation(options = {}) {
    return this.client.post("/salesforce/sync/line-information", options);
  }

  async syncSalesforcePortsOrders(options = {}) {
    return this.client.post("/salesforce/sync/ports-orders", options);
  }

  // ============= Activities & Engagement =============

  async syncSalesforceEvents(options = {}) {
    return this.client.post("/salesforce/sync/events", options);
  }

  async syncSalesforceTasks(options = {}) {
    return this.client.post("/salesforce/sync/tasks", options);
  }

  // ============= Customer Service =============

  async syncSalesforceCases(options = {}) {
    return this.client.post("/salesforce/sync/cases", options);
  }

  // ============= Files & Documents =============

  async syncSalesforceFiles(options = {}) {
    return this.client.post("/salesforce/sync/files", options);
  }

  async syncSalesforceAttachments(options = {}) {
    return this.client.post("/salesforce/sync/attachments", options);
  }

  // ============= Sync Status & Monitoring =============

  async getSalesforceSyncStatus(sessionId) {
    return this.client.get(`/salesforce/sync/status/${sessionId}`);
  }
  async previewAutoAssignment(config) {
    return this.client.post("/leads/auto-assign/preview", config);
  }

  async executeAutoAssignment(config) {
    return this.client.post("/leads/auto-assign/execute", config);
  }

  async dryRunAutoAssignment(config) {
    return this.client.post("/leads/auto-assign/dry-run", config);
  }

  async getAssignmentHistory(params = {}) {
    return this.client.get("/leads/auto-assign/history", { params });
  }

  async getUserAssignmentStats(userId) {
    return this.client.get(`/leads/auto-assign/stats/${userId}`);
  }

  async getSalesEngagementLeads(params = {}) {
    return this.client.get("/sales-engagement/leads", { params });
  }
  async getSalesEngagementLeadsAdvanced(params = {}) {
    return this.client.get("/sales-engagement/leads/advanced", { params });
  }
  async logSalesEngagementCall(leadId, callData) {
    return this.client.post(
      `/sales-engagement/leads/${leadId}/log-call`,
      callData,
    );
  }

  async getSalesEngagementStats(params = {}) {
    return this.client.get("/sales-engagement/stats", { params });
  }

  async resendEngagementEmail(leadId) {
    return this.client.post(`/sales-engagement/leads/${leadId}/resend-email`);
  }

  // ============= Email Templates =============

  async getEmailTemplates(params = {}) {
    return this.client.get("/email-templates", { params });
  }

  async getEmailTemplate(templateId) {
    return this.client.get(`/email-templates/${templateId}`);
  }

  async createEmailTemplate(templateData) {
    return this.client.post("/email-templates", templateData);
  }

  async updateEmailTemplate(templateId, templateData) {
    return this.client.put(`/email-templates/${templateId}`, templateData);
  }

  async deleteEmailTemplate(templateId) {
    return this.client.delete(`/email-templates/${templateId}`);
  }

  async getEmailConnectionStatus() {
    return this.client.get("/email-connection/status");
  }

  async getEmailAuthUrl(params = {}) {
    return this.client.get("/email-connection/auth-url", { params });
  }

  async handleEmailCallback(data) {
    return this.client.post("/email-connection/callback", data);
  }
  async getCalendarTeams() {
    return this.client.get("/teams/calendar-teams");
  }
  async disconnectEmail() {
    return this.client.delete("/email-connection");
  }

  async testEmailConnection() {
    return this.client.post("/email-connection/test");
  }
  async lookupByPhone(phoneNumber) {
    return this.client.get(
      `/call-integration/lookup/${encodeURIComponent(phoneNumber)}`,
    );
  }

  async logCall(callData) {
    return this.client.post("/call-integration/log", callData);
  }

  async testCallIntegration() {
    return this.client.get("/call-integration/test");
  }

  async getReportTypes() {
    return this.client.get("/reports/types");
  }

  async getReportFolders() {
    return this.client.get("/reports/folders");
  }

  async createReportFolder(folderData) {
    return this.client.post("/reports/folders", folderData);
  }

  async getReports(params = {}) {
    return this.client.get("/reports", { params });
  }

  async getReport(reportId) {
    return this.client.get(`/reports/${reportId}`);
  }

  async createReport(reportData) {
    return this.client.post("/reports", reportData);
  }

  async updateReport(reportId, reportData) {
    return this.client.put(`/reports/${reportId}`, reportData);
  }

  async deleteReport(reportId) {
    return this.client.delete(`/reports/${reportId}`);
  }

  async runReport(
    reportId,
    runtimeFilters = {},
    forceRefresh = false,
    periodOffset = 0,
  ) {
    const params = new URLSearchParams();
    if (forceRefresh) {
      params.append("forceRefresh", "true");
    }
    if (periodOffset !== 0) {
      params.append("periodOffset", periodOffset.toString());
    }

    const url = `/reports/${reportId}/run${params.toString() ? `?${params.toString()}` : ""}`;
    return this.client.post(url, runtimeFilters);
  }

  // Add method to get available periods
  async getAvailablePeriods() {
    return this.client.get("/reports/available-periods");
  }

  async exportReport(reportId, format = "csv", runtimeFilters = {}) {
    return this.client.post(
      `/reports/${reportId}/export?format=${format}`,
      runtimeFilters,
      {
        responseType: "blob",
      },
    );
  }

  async cloneReport(reportId, name) {
    return this.client.post(`/reports/${reportId}/clone`, { name });
  }

  async subscribeToReport(reportId, subscriptionData) {
    return this.client.post(`/reports/${reportId}/subscribe`, subscriptionData);
  }

  async unsubscribeFromReport(reportId) {
    return this.client.delete(`/reports/${reportId}/subscribe`);
  }

  async getReportRuns(reportId, params = {}) {
    return this.client.get(`/reports/${reportId}/runs`, { params });
  }

  async syncSalesforceReports() {
    return this.client.post("/salesforce/sync/reports");
  }
  // In api.js, add these methods:

  async createSalesReports() {
    return this.client.post("/reports/create-sales-reports");
  }

  async getCurrentMonthPeriod() {
    return this.client.get("/reports/utils/current-month-period");
  }
  // In your ApiService class, add these methods:

  // ============= Email Sending =============

  async sendEmail(emailData) {
    return this.client.post("/email/send", emailData);
  }

  async sendTemplateEmail(templateId, emailData) {
    return this.client.post("/email/send-template", {
      templateId,
      ...emailData,
    });
  }

  async getEmailLogs(params = {}) {
    return this.client.get("/email/logs", { params });
  }

  async getEmailLog(emailLogId) {
    return this.client.get(`/email/logs/${emailLogId}`);
  }
  // Add this method to your ApiService class in api.js
  async changePassword(data) {
    return this.client.post("/auth/change-password", data);
  }

  // --- ADDED: Regenerate Token Method ---
  async regenerateToken() {
    return this.client.post("/auth/regenerate-token");
  }
  // --------------------------------------

  async getCaseAppointments(caseId) {
    return this.client.get(`/cases/${caseId}/appointments`);
  }

  async createAppointmentFromCase(appointmentData) {
    return this.client.post("/appointments/from-case", appointmentData);
  }
  // Add to api.js ApiService class

  // ============= Case Assignment =============

  async assignCase(caseId, ownerId, teamId = null, notes = null) {
    return this.client.post(`/cases/${caseId}/assign`, {
      ownerId,
      teamId,
      notes,
    });
  }

  async bulkAssignCases(caseIds, ownerId, teamId = null) {
    return this.client.post("/cases/bulk-assign", {
      caseIds,
      ownerId,
      teamId,
    });
  }

  async previewCaseAutoAssignment(config) {
    return this.client.post("/cases/auto-assign/preview", config);
  }

  async executeCaseAutoAssignment(config) {
    return this.client.post("/cases/auto-assign/execute", config);
  }

  async getCaseAssignmentHistory(params = {}) {
    return this.client.get("/cases/auto-assign/history", { params });
  }

  async getUserCaseAssignmentStats(userId) {
    return this.client.get(`/cases/auto-assign/stats/${userId}`);
  }
  // Add to api.js
  async createCaseReports() {
    return this.client.post("/reports/create-case-reports");
  }

  async createUpgradeReports() {
    return this.client.post("/reports/create-upgrade-reports");
  }
  // In api.js, add this method to the ApiService class:

  async getReportsByType(reportType) {
    return this.client.get("/reports/by-type", {
      params: { reportType, isPublic: true },
    });
  }

  // Get open cases for an account
  async getAccountOpenCases(accountId) {
    return this.client.get(`/cases/accounts/${accountId}/open-cases`);
  }
  async sendCaseEmail(caseId, emailData) {
    return this.client.post(`/case-emails/${caseId}/emails/send`, emailData);
  }
  // ============= Ports Orders =============

  async getPortsOrders(params = {}) {
    return this.client.get("/ports-orders", { params });
  }

  async getPortsOrder(portsOrderId) {
    return this.client.get(`/ports-orders/${portsOrderId}`);
  }

  async createPortsOrder(portsOrderData) {
    return this.client.post("/ports-orders", portsOrderData);
  }

  async updatePortsOrder(portsOrderId, portsOrderData) {
    return this.client.put(`/ports-orders/${portsOrderId}`, portsOrderData);
  }

  async deletePortsOrder(portsOrderId) {
    return this.client.delete(`/ports-orders/${portsOrderId}`);
  }

  async getPortsOrdersByAccount(accountId) {
    return this.client.get("/ports-orders", { params: { accountId } });
  }
  async sendCaseTemplateEmail(caseId, templateId, emailData) {
    return this.client.post(`/case-emails/${caseId}/emails/send-template`, {
      templateId,
      ...emailData,
    });
  }

  async replyCaseEmail(caseId, emailLogId, replyData) {
    return this.client.post(
      `/case-emails/${caseId}/emails/${emailLogId}/reply`,
      replyData,
    );
  }

  async getCaseEmails(caseId, params = {}) {
    return this.client.get(`/case-emails/${caseId}/emails`, { params });
  }

  async getCaseEmailThread(caseId) {
    return this.client.get(`/case-emails/${caseId}/emails/thread`);
  }
  async getVisitNotifications(params = {}) {
    return this.client.get("/visit-notifications", { params });
  }

  async getVisitNotification(notificationId) {
    return this.client.get(`/visit-notifications/${notificationId}`);
  }

  async updateVisitNotificationStatus(notificationId, statusData) {
    return this.client.put(
      `/visit-notifications/${notificationId}/status`,
      statusData,
    );
  }

  async addVisitNotificationComment(notificationId, commentData) {
    return this.client.post(
      `/visit-notifications/${notificationId}/comments`,
      commentData,
    );
  }

  async getVisitNotificationStats(params = {}) {
    return this.client.get("/visit-notifications/stats", { params });
  }
  async getSalesTeamMembers() {
    return this.client.get("/teams/sales-team-members");
  }

  async cloneOpportunity(opportunityId, customName = null) {
    return this.client.post(`/opportunities/${opportunityId}/clone`, {
      name: customName,
    });
  }
  // Get detail records for a specific group member
  // AFTER
  async getGroupMemberDetails(
    reportId,
    groupField,
    groupValue,
    filters = {},
    periodOffset = 0,
  ) {
    const params = new URLSearchParams({
      groupField: groupField,
      groupValue: groupValue,
    });
    if (periodOffset !== 0) {
      params.append("periodOffset", periodOffset.toString());
    }
    return this.client.post(
      `/reports/${reportId}/group-details?${params.toString()}`,
      filters,
    );
  }

  async getProjectFiles(projectId) {
    return this.client.get(`/projects/${projectId}/files`);
  }

  async uploadProjectFile(projectId, formData) {
    return this.client.post(`/projects/${projectId}/files`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  async deleteProjectFile(projectId, fileId) {
    return this.client.delete(`/projects/${projectId}/files/${fileId}`);
  }
  async getCaseFiles(caseId) {
    return this.client.get(`/cases/${caseId}/files`);
  }

  async uploadCaseFile(caseId, formData) {
    return this.client.post(`/cases/${caseId}/files`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  async deleteCaseFile(caseId, fileId) {
    return this.client.delete(`/cases/${caseId}/files/${fileId}`);
  }

  async createTeamTask(data) {
    return this.client.post("/team-tasks", data);
  }

  async getMyTeamTasks(params = {}) {
    return this.client.get("/team-tasks/my-tasks", { params });
  }
  async getCreatedTeamTasks(params = {}) {
    return this.client.get("/team-tasks/created", { params });
  }
  async getTeamTask(id) {
    return this.client.get(`/team-tasks/${id}`);
  }

  async markTeamTaskAsRead(id) {
    return this.client.post(`/team-tasks/${id}/mark-read`);
  }

  async updateTeamTaskStatus(id, data) {
    return this.client.put(`/team-tasks/${id}/status`, data);
  }

  async addTeamTaskComment(id, data) {
    return this.client.post(`/team-tasks/${id}/comments`, data);
  }

  async getTeamTaskUnreadCount() {
    return this.client.get("/team-tasks/unread-count");
  }

  async getTeamTaskStats() {
    return this.client.get("/team-tasks/stats");
  }

  async getEmailTemplates(params = {}) {
    return this.client.get("/email-templates", { params });
  }

  async getEmailTemplate(templateId) {
    return this.client.get(`/email-templates/${templateId}`);
  }

  async createEmailTemplate(templateData) {
    return this.client.post("/email-templates", templateData);
  }

  async updateEmailTemplate(templateId, templateData) {
    return this.client.put(`/email-templates/${templateId}`, templateData);
  }

  async deleteEmailTemplate(templateId) {
    return this.client.delete(`/email-templates/${templateId}`);
  }

  async getEmailTemplateTypes() {
    return this.client.get("/email-templates/types");
  }

  // ============= LEAD EMAILS =============
  async sendLeadEmail(leadId, emailData) {
    return this.client.post(`/leads/${leadId}/emails/send`, emailData);
  }

  async sendLeadTemplateEmail(leadId, templateId, emailData) {
    return this.client.post(`/leads/${leadId}/emails/send-template`, {
      templateId,
      ...emailData,
    });
  }

  async replyToLeadEmail(leadId, emailLogId, replyData) {
    return this.client.post(
      `/leads/${leadId}/emails/${emailLogId}/reply`,
      replyData,
    );
  }

  async getLeadEmails(leadId, params = {}) {
    return this.client.get(`/leads/${leadId}/emails`, { params });
  }

  async getLeadEmailThread(leadId) {
    return this.client.get(`/leads/${leadId}/emails/thread`);
  }
  async sendAccountEmail(accountId, emailData) {
    return this.client.post(`/accounts/${accountId}/emails/send`, emailData);
  }

  async sendAccountTemplateEmail(accountId, templateId, emailData) {
    return this.client.post(`/accounts/${accountId}/emails/send-template`, {
      templateId,
      ...emailData,
    });
  }

  async replyAccountEmail(accountId, emailLogId, replyData) {
    return this.client.post(
      `/accounts/${accountId}/emails/${emailLogId}/reply`,
      replyData,
    );
  }

  async getAccountEmails(accountId, params = {}) {
    return this.client.get(`/accounts/${accountId}/emails`, { params });
  }

  async getAccountEmailThread(accountId) {
    return this.client.get(`/accounts/${accountId}/emails/thread`);
  }

  // Add these methods to your api.js file in the ApiService class

  // ========== SALESFORCE ATTACHMENTS ==========

  async getAllSalesforceAttachments(params = {}) {
    return this.client.get("/salesforce-attachments", { params });
  }

  async getSalesforceAttachment(id) {
    return this.client.get(`/salesforce-attachments/${id}`);
  }

  async getSalesforceAttachmentsByParent(parentType, parentId) {
    return this.client.get(
      `/salesforce-attachments/parent/${parentType}/${parentId}`,
    );
  }

  async updateSalesforceAttachment(id, data) {
    return this.client.put(`/salesforce-attachments/${id}`, data);
  }

  async deleteSalesforceAttachment(id) {
    return this.client.delete(`/salesforce-attachments/${id}`);
  }

  async getSalesforceAttachmentStats() {
    return this.client.get("/salesforce-attachments/stats");
  }
  async findDuplicates(threshold = 0.85) {
    return this.client.get("/accounts/duplicates", {
      params: { threshold },
    });
  }

  async previewMerge(sourceId, targetId) {
    return this.client.get(`/accounts/merge/${sourceId}/${targetId}/preview`);
  }

  async mergeAccounts(sourceId, targetId, options = {}) {
    return this.client.post(`/accounts/merge/${sourceId}/${targetId}`, options);
  }
  // In api.js - Add these methods to ApiService class

  /**
   * Find potential duplicates for a specific account (optimized)
   * Only searches for duplicates of the current account
   */
  async findAccountDuplicates(accountId, threshold = 0.4) {
    return this.client.get(`/accounts/${accountId}/find-duplicates`, {
      params: { threshold },
    });
  }

  /**
   * Preview merge for specific account
   */
  async previewAccountMerge(accountId, targetId) {
    return this.client.get(`/accounts/${accountId}/merge/${targetId}/preview`);
  }

  /**
   * Merge specific account into target
   */
  async mergeAccountInto(accountId, targetId, options = {}) {
    return this.client.post(
      `/accounts/${accountId}/merge/${targetId}`,
      options,
    );
  }

  /**
   * Get all email templates with optional filters
   * @param {Object} params - Query parameters
   * @param {string} params.type - Filter by template type
   * @param {boolean} params.isActive - Filter by active status
   * @param {boolean} params.isDefault - Filter by default templates
   * @param {string} params.category - Filter by category
   * @param {string} params.search - Search query
   * @param {number} params.page - Page number for pagination
   * @param {number} params.limit - Items per page
   */
  async getCompanyEmailTemplates(params = {}) {
    return this.client.get("/email-template", { params });
  }

  /**
   * Get a single email template by ID
   * @param {string} templateId - Template ID
   */
  async getCompanyEmailTemplate(templateId) {
    return this.client.get(`/email-template/${templateId}`);
  }

  /**
   * Get email template statistics
   */
  async getCompanyEmailTemplateStats() {
    return this.client.get("/email-template/stats");
  }

  /**
   * Create a new email template
   * @param {Object} templateData - Template data
   * @param {string} templateData.name - Template name
   * @param {string} templateData.subject - Email subject
   * @param {string} templateData.body - Plain text body
   * @param {string} templateData.bodyHtml - HTML body
   * @param {string} templateData.description - Template description
   * @param {string} templateData.type - Template type
   * @param {string} templateData.category - Template category
   * @param {Array} templateData.variables - Available variables
   * @param {boolean} templateData.isActive - Active status
   * @param {boolean} templateData.isDefault - Default template flag
   */
  async createCompanyEmailTemplate(templateData) {
    return this.client.post("/email-template", templateData);
  }

  /**
   * Update an existing email template
   * @param {string} templateId - Template ID
   * @param {Object} templateData - Updated template data
   */
  async updateCompanyEmailTemplate(templateId, templateData) {
    return this.client.put(`/email-template/${templateId}`, templateData);
  }

  /**
   * Delete an email template (soft delete if used, hard delete if never used)
   * @param {string} templateId - Template ID
   */
  async deleteCompanyEmailTemplate(templateId) {
    return this.client.delete(`/email-template/${templateId}`);
  }

  /**
   * Duplicate an existing email template
   * @param {string} templateId - Template ID to duplicate
   */
  async duplicateCompanyEmailTemplate(templateId) {
    return this.client.post(`/email-template/${templateId}/duplicate`);
  }

  /**
   * Send an email using a template
   * @param {string} templateId - Template ID
   * @param {Object} emailData - Email sending data
   * @param {string|Array} emailData.to - Recipient email(s)
   * @param {Object} emailData.variables - Variable values for template
   * @param {string} emailData.leadId - Optional lead ID
   * @param {string} emailData.opportunityId - Optional opportunity ID
   * @param {string} emailData.appointmentId - Optional appointment ID
   * @param {string} emailData.accountId - Optional account ID
   */
  async sendCompanyEmailTemplate(templateId, emailData) {
    return this.client.post(`/email-template/${templateId}/send`, emailData);
  }

  // Add this method to the ApiService class in api.js

  async updateAppointmentQualificationScore(appointmentId, qualificationScore) {
    return this.client.put(
      `/appointments/${appointmentId}/qualification-score`,
      {
        qualificationScore,
      },
    );
  }
  async getOpportunityVisits(opportunityId) {
    return this.client.get(`/opportunities/${opportunityId}/visits`);
  }

  // Add these methods to your api.js service

  // ============= Lead Quarantine Methods =============

  /**
   * Quarantine a single lead
   * @param {string} leadId - Lead ID
   * @param {object} quarantineData - { quarantineType, customDuration, reason }
   */
  async quarantineLead(leadId, quarantineData) {
    return this.client.post(`/leads/${leadId}/quarantine`, quarantineData);
  }

  /**
   * Bulk quarantine leads
   * @param {object} bulkData - { leadIds, quarantineType, customDuration, reason }
   */
  async bulkQuarantineLeads(bulkData) {
    return this.client.post("/leads/bulk-quarantine", bulkData);
  }

  /**
   * Release a lead from quarantine
   * @param {string} leadId - Lead ID
   */
  async releaseFromQuarantine(leadId) {
    return this.client.post(`/leads/${leadId}/release-quarantine`);
  }

  /**
   * Update quarantine duration
   * @param {string} leadId - Lead ID
   * @param {number} durationInDays - New duration in days
   */
  async updateQuarantineDuration(leadId, durationInDays) {
    return this.client.put(`/leads/${leadId}/quarantine/duration`, {
      durationInDays,
    });
  }

  /**
   * Get quarantine statistics
   */
  async getQuarantineStats() {
    return this.client.get("/leads/quarantine/stats");
  }

  /**
   * Get quarantined leads list
   * @param {object} params - { page, limit, quarantineType, sortBy, sortOrder }
   */
  async getQuarantinedLeads(params = {}) {
    return this.client.get("/leads/quarantine/list", { params });
  }
  async getCaseFiles(caseId) {
    return this.client.get(`/cases/${caseId}/files`);
  }

  async uploadCaseFile(caseId, formData) {
    return this.client.post(`/cases/${caseId}/files`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  async deleteCaseFile(caseId, fileId) {
    return this.client.delete(`/cases/${caseId}/files/${fileId}`);
  }
  // Add to ApiService class in api.js

  async getAllCompanyTeams() {
    return this.client.get("/teams/all-company-teams");
  }
  async getAccountAppointments(accountId) {
    return this.client.get(`/accounts/${accountId}/appointments`);
  }

  async initializeQuoteIntegration() {
    // This happens automatically on CRM login via token interceptor
    // But can be called manually to verify integration
    return this.client.get("/quote-integration/status");
  }

  async initializeQuote(opportunityId, quoteToken) {
    // quoteToken is the user's Quote system bearer token, stored in the CRM's
    // localStorage by QuoteAuthModal after a successful manual login.
    // We forward it in a custom header so the CRM backend can pass it straight
    // to the Quote API — the CRM never needs to store it server-side.
    return this.client.post(
      `/opportunities/${opportunityId}/initialize-quote`,
      {},
      { headers: quoteToken ? { "X-Quote-Token": quoteToken } : {} },
    );
  }

  async getOpportunityQuote(opportunityId) {
    const quoteToken = localStorage.getItem("quoteAuthToken");
    return this.client.get(`/opportunities/${opportunityId}/quote`, {
      headers: quoteToken ? { "X-Quote-Token": quoteToken } : {},
    });
  }

  async syncQuoteStatus(opportunityId) {
    return this.client.post(
      `/opportunities/${opportunityId}/sync-quote-status`,
    );
  }

  async getActiveDailyCallbackFilter() {
    try {
      const response = await this.client.get("/leads/filter-presets", {
        params: { entityType: "lead" },
      });
      const presets = response.data || [];
      const active = presets.find(
        (p) => p.name?.startsWith("__callback_daily__") && p.isDefault,
      );
      return active ? active.filterConfig : null;
    } catch (err) {
      console.error("Failed to load daily callback filter:", err);
      return null; // Fail silently — don't break MyLeads if this errors
    }
  }
  async setCaseReminder(caseId, enabled, note = null) {
    return this.client.put(`/cases/${caseId}/reminder`, { enabled, note });
  }
  /**
   * Toggle the "contacted" flag on a case.
   * @param {string} caseId
   * @param {boolean} contacted  true = mark contacted, false = unmark
   */
  async markCaseContacted(caseId, contacted) {
    return this.client.post(`/cases/${caseId}/mark-contacted`, { contacted });
  }
  async uploadNoteImage(caseId, noteId, formData) {
    return this.client.post(
      `/cases/${caseId}/notes/${noteId}/images`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
  }

  async deleteNoteImage(caseId, noteId, s3Key) {
    return this.client.delete(
      `/cases/${caseId}/notes/${noteId}/images/${encodeURIComponent(s3Key)}`,
    );
  }
  async getAccountPhoneSystems(accountId) {
    return this.client.get(`/accounts/${accountId}/phone-systems`);
  }

  async createPhoneSystem(accountId, phoneSystemData) {
    return this.client.post(
      `/accounts/${accountId}/phone-systems`,
      phoneSystemData,
    );
  }

  async updatePhoneSystem(accountId, phoneSystemId, phoneSystemData) {
    return this.client.put(
      `/accounts/${accountId}/phone-systems/${phoneSystemId}`,
      phoneSystemData,
    );
  }

  async deletePhoneSystem(accountId, phoneSystemId) {
    return this.client.delete(
      `/accounts/${accountId}/phone-systems/${phoneSystemId}`,
    );
  }
  async moveToCleansed(leadId) {
    return this.client.post(`/leads/${leadId}/move-to-cleansed`);
  }

  async getCleansedLeads(params = {}) {
    return this.client.get("/cleansed-leads", { params });
  }
  async getCleansedLead(id) {
    return this.client.get(`/cleansed-leads/${id}`);
  }

  async updateCleansedLead(id, data) {
    return this.client.put(`/cleansed-leads/${id}`, data);
  }

  async deleteCleansedLead(id) {
    return this.client.delete(`/cleansed-leads/${id}`);
  }
  /**
   * Get the Webex OAuth authorisation URL from the backend.
   * The admin is then redirected to this URL for Webex consent.
   */
  async getWebexAuthUrl() {
    return this.client.get("/webex-integration/connect");
  }

  /**
   * Get current Webex integration status for the company.
   * Returns: { enabled, configured, webhookId, webhookUrl, tokenExpiresAt }
   */
  async getWebexIntegrationStatus() {
    return this.client.get("/webex-integration/status");
  }

  /**
   * Disconnect the Webex integration — deletes the webhook and clears stored tokens.
   */
  async disconnectWebexIntegration() {
    return this.client.delete("/webex-integration/disconnect");
  }

  async getCleansedLeadNotes(id) {
    return this.client.get(`/cleansed-leads/${id}/notes`);
  }

  async createCleansedLeadNote(id, noteData) {
    return this.client.post(`/cleansed-leads/${id}/notes`, noteData);
  }

  async createCallNote(callNoteData) {
    return this.client.post("/call-notes", callNoteData);
  }

  async getCallNotes(entityType, entityId, params = {}) {
    return this.client.get(`/call-notes/${entityType}/${entityId}`, { params });
  }

  async updateCallNote(callNoteId, updateData) {
    return this.client.put(`/call-notes/${callNoteId}`, updateData);
  }

  async deleteCallNote(callNoteId) {
    return this.client.delete(`/call-notes/${callNoteId}`);
  }
  async toggleUserStatus(userId) {
    return this.client.patch(`/teams/users/${userId}/toggle-status`);
  }
  // In api.js ApiService class

  // ============= Webex Compliance Integration =============

  async getWebexComplianceStatus() {
    return this.client.get("/webex-compliance/status");
  }

  async connectWebexCompliance(complianceOfficerId) {
    return this.client.post("/webex-compliance/connect", {
      complianceOfficerId,
    });
  }

  async disconnectWebexCompliance() {
    return this.client.delete("/webex-compliance/disconnect");
  }

  async getAppointmentActivity(appointmentId) {
    return this.client.get(`/appointments/${appointmentId}/activity`);
  }

  /**
   * Manually fetch and store calls from the external call system
   * for a specific appointment, matched by the appointment's phone numbers.
   *
   * @param {string} appointmentId - UUID of the appointment
   * @param {number} [days=90]     - How far back to search in the external system
   */
  async fetchCallsForAppointment(appointmentId, days = 30) {
    return this.client.post(
      `/appointment-call-sync/fetch-for-appointment/${appointmentId}`,
      undefined,
      { params: { days } },
    );
  }
  async previewUnqualifiedAutoAssignment(config) {
    return this.client.post("/leads/unqualified-auto-assign/preview", config);
  }

  async executeUnqualifiedAutoAssignment(config) {
    return this.client.post("/leads/unqualified-auto-assign/execute", config);
  }

  async dryRunUnqualifiedAutoAssignment(config) {
    return this.client.post("/leads/unqualified-auto-assign/dry-run", config);
  }

  /**
   * Toggle the reminder flag on a task.
   * @param {string} taskId
   * @param {boolean} enabled  true = enable reminder, false = disable
   */
  async setTaskReminder(taskId, enabled) {
    return this.client.patch(`/tasks/${taskId}`, { reminderEnabled: enabled });
  }
  // ============= Digital Quotes =============

  async getDigitalQuotes(params = {}) {
    return this.client.get("/quotes", { params });
  }

  async getDigitalQuote(quoteId) {
    return this.client.get(`/quotes/${quoteId}`);
  }

  async createDigitalQuote(quoteData) {
    return this.client.post("/quotes", quoteData);
  }

  async updateDigitalQuote(quoteId, quoteData) {
    return this.client.put(`/quotes/${quoteId}`, quoteData);
  }

  async deleteDigitalQuote(quoteId) {
    return this.client.delete(`/quotes/${quoteId}`);
  }

  async sendDigitalQuote(quoteId) {
    return this.client.post(`/quotes/${quoteId}/send`);
  }

  async initializeDigitalQuoteFromOpportunity(opportunityId) {
    return this.client.get(`/quotes/initialize/opportunity/${opportunityId}`);
  }

  // Services catalog
  async getQuoteServices(params = {}) {
    return this.client.get("/quotes/services", { params });
  }
  async getQuoteService(serviceId) {
    return this.client.get(`/quotes/services/${serviceId}`);
  }
  async createQuoteService(serviceData) {
    return this.client.post("/quotes/services", serviceData);
  }
  async updateQuoteService(serviceId, serviceData) {
    return this.client.put(`/quotes/services/${serviceId}`, serviceData);
  }
  async deleteQuoteService(serviceId) {
    return this.client.delete(`/quotes/services/${serviceId}`);
  }

  // Public (no auth) — use a bare axios call
  async getPublicQuote(token) {
    const axios = (await import("axios")).default;
    const API_URL =
      process.env.REACT_APP_API_URL || "http://localhost:5000/api";
    return axios.get(`${API_URL}/public/quotes/${token}`).then((r) => r.data);
  }

  async signPublicQuote(token, signatureData) {
    const axios = (await import("axios")).default;
    const API_URL =
      process.env.REACT_APP_API_URL || "http://localhost:5000/api";
    return axios
      .post(`${API_URL}/public/quotes/${token}/sign`, signatureData)
      .then((r) => r.data);
  }

  async rejectPublicQuote(token, reason) {
    const axios = (await import("axios")).default;
    const API_URL =
      process.env.REACT_APP_API_URL || "http://localhost:5000/api";
    return axios
      .post(`${API_URL}/public/quotes/${token}/reject`, { reason })
      .then((r) => r.data);
  }

  async trackPublicQuoteDuration(token, durationSeconds) {
    const axios = (await import("axios")).default;
    const API_URL =
      process.env.REACT_APP_API_URL || "http://localhost:5000/api";
    return axios
      .post(`${API_URL}/public/quotes/${token}/track`, { durationSeconds })
      .then((r) => r.data);
  }
  // ============= APPOINTMENT TARGETS =============

  async getUserAppointmentTargets(userId, params = {}) {
    return this.client.get(`/teams/users/${userId}/appointment-targets`, {
      params,
    });
  }

  async updateUserAppointmentTargets(userId, data) {
    return this.client.put(`/teams/users/${userId}/appointment-targets`, data);
  }

  async getTeamAppointmentTargets(teamId, params = {}) {
    return this.client.get(`/teams/appointment-targets/team/${teamId}`, {
      params,
    });
  }

  async bulkUpdateAppointmentTargets(data) {
    return this.client.post("/teams/appointment-targets/bulk-update", data);
  }

  async getAppointmentTargetStats(params = {}) {
    return this.client.get("/teams/appointment-targets/stats", { params });
  }
  // ─────────────────────────────────────────────────────────────────────────────
  // PASTE THESE METHODS INTO YOUR ApiService CLASS IN services/api.js
  // Add them before the closing brace of the class, before:
  //   const apiService = new ApiService();
  //   export default apiService;
  // ─────────────────────────────────────────────────────────────────────────────

  // ============= Dashboards =============

  async getDashboards() {
    return this.client.get("/dashboards");
  }

  async getDashboard(dashboardId) {
    return this.client.get(`/dashboards/${dashboardId}`);
  }

  async createDashboard(dashboardData) {
    return this.client.post("/dashboards", dashboardData);
  }

  async updateDashboard(dashboardId, dashboardData) {
    return this.client.put(`/dashboards/${dashboardId}`, dashboardData);
  }

  async deleteDashboard(dashboardId) {
    return this.client.delete(`/dashboards/${dashboardId}`);
  }

  async saveDashboardLayout(dashboardId, layout) {
    return this.client.put(`/dashboards/${dashboardId}/layout`, { layout });
  }

  // ============= Dashboard Widgets =============

  async addDashboardWidget(dashboardId, widgetData) {
    return this.client.post(`/dashboards/${dashboardId}/widgets`, widgetData);
  }

  async updateDashboardWidget(dashboardId, widgetId, widgetData) {
    return this.client.put(
      `/dashboards/${dashboardId}/widgets/${widgetId}`,
      widgetData,
    );
  }

  async removeDashboardWidget(dashboardId, widgetId) {
    return this.client.delete(`/dashboards/${dashboardId}/widgets/${widgetId}`);
  }

  // ============= TV Displays (authenticated — admin side) =============

  async getTVDisplays() {
    return this.client.get("/dashboards/tvs");
  }

  async createTVPairingCode(dashboardId, name) {
    return this.client.post("/dashboards/tvs/pairing-code", {
      dashboardId,
      name,
    });
  }

  async pairTV(pairingCode, dashboardId) {
    return this.client.post("/dashboards/tvs/pair", {
      pairingCode,
      dashboardId,
    });
  }

  async getTVStatus(tvId) {
    return this.client.get(`/dashboards/tvs/${tvId}/status`);
  }

  async getMyAppointmentTargets(params = {}) {
    return this.client.get("/teams/my-appointment-targets", { params });
  }
  async getTeamPerformanceConfig(teamId) {
    return this.client.get(`/teams/${teamId}/performance-config`);
  }

  async updateTeamPerformanceConfig(teamId, data) {
    return this.client.put(`/teams/${teamId}/performance-config`, data);
  }

  async getPerformanceStats(params = {}) {
    return this.client.get("/teams/performance-stats", { params });
  }

  async getMyPerformance(params = {}) {
    return this.client.get("/teams/my-performance", { params });
  }
  // ═══ Performance Targets v2 ══════════════════════════════════════════════════

  async getTeamTargets(teamId, params = {}) {
    return this.client.get(`/teams/${teamId}/targets`, { params });
  }

  async upsertTeamTarget(teamId, data) {
    return this.client.put(`/teams/${teamId}/targets`, data);
  }

  async upsertMemberTarget(teamId, userId, data) {
    return this.client.put(`/teams/${teamId}/members/${userId}/targets`, data);
  }

  async deleteMemberTarget(teamId, userId, targetId) {
    return this.client.delete(
      `/teams/${teamId}/members/${userId}/targets/${targetId}`,
    );
  }

  async bulkUpsertTargets(teamId, updates) {
    return this.client.post(`/teams/${teamId}/targets/bulk`, { updates });
  }

  async uploadDashboardLogo(dashboardId, file) {
    const formData = new FormData();
    formData.append("logo", file);
    return this.client.post(`/dashboards/${dashboardId}/logo`, formData);
  }

  async runOpportunityCreditCheck(opportunityId, body = {}) {
    return this.client.post(
      `/opportunities/${opportunityId}/credit-check`,
      body,
    );
  }

  /**
   * Fetch the stored Creditsafe result for an opportunity.
   * Returns { success, creditCheck } — creditCheck is null if not yet run.
   */
  async getOpportunityCreditCheck(opportunityId) {
    return this.client.get(`/opportunities/${opportunityId}/credit-check`);
  }

  // ─── ADD TO YOUR ApiService CLASS IN services/api.js ─────────────────────────
  // Base route: /broadband-search

  // Eclipse only — address lookup before availability (no DB write)
  async eclipseAddressLookup(postCode) {
    return this.client.get("/broadband-search/eclipse/address-lookup", {
      params: { postCode },
    });
  }

  // List all searches (supports ?provider=ITS&status=ordered&search=&page=&limit=)
  async getBroadbandSearches(params = {}) {
    return this.client.get("/broadband-search", { params });
  }

  // Get single search record
  async getBroadbandSearch(id) {
    return this.client.get(`/broadband-search/${id}`);
  }

  // Run availability search — ITS or Eclipse
  // ITS:     { provider:"ITS", postcode, uprn?, ..., label?, linkedAccountId? }
  // Eclipse: { provider:"ECLIPSE", addressReference:{...}, label?, linkedAccountId? }
  async runBroadbandSearch(payload) {
    return this.client.post("/broadband-search/search", payload);
  }

  // Delete a search record
  async deleteBroadbandSearch(id) {
    return this.client.delete(`/broadband-search/${id}`);
  }

  // ITS only ──────────────────────────────────────────────────────────────────

  async saveBroadbandQuote(id, payload) {
    // payload: { quoteId, customer_name, custom_reference?, layer?, addons? }
    return this.client.post(`/broadband-search/${id}/save-quote`, payload);
  }

  async acceptBroadbandQuote(id) {
    return this.client.post(`/broadband-search/${id}/accept-quote`);
  }

  async getBroadbandQuoteConfig(id) {
    return this.client.get(`/broadband-search/${id}/quote-config`);
  }

  async submitBroadbandData(id, payload) {
    return this.client.post(`/broadband-search/${id}/submit-data`, payload);
  }

  async approveBroadbandOrder(id) {
    return this.client.post(`/broadband-search/${id}/approve`);
  }

  // Eclipse / non-API providers ───────────────────────────────────────────────

  // payload: { selectedProductCode?, providerOrderRef?, monthlyCost?, notes? }
  async markBroadbandOrdered(id, payload) {
    return this.client.post(`/broadband-search/${id}/mark-ordered`, payload);
  }

  // All providers ─────────────────────────────────────────────────────────────

  async cancelBroadbandSearch(id, reason) {
    return this.client.post(`/broadband-search/${id}/cancel`, { reason });
  }
  // ─── ADD THESE METHODS TO ApiService CLASS IN api.js ────────────────────────

  // Installs
  async getInstalls(params = {}) {
    return this.client.get("/installs", { params });
  }

  async getInstall(installId) {
    return this.client.get(`/installs/${installId}`);
  }

  async getInstallStats() {
    return this.client.get("/installs/stats");
  }

  async createInstall(data) {
    return this.client.post("/installs", data);
  }

  async updateInstall(installId, data) {
    return this.client.put(`/installs/${installId}`, data);
  }

  async deleteInstall(installId) {
    return this.client.delete(`/installs/${installId}`);
  }

  async importInstallsXlsx(file) {
    const formData = new FormData();
    formData.append("file", file);
    return this.client.post("/installs/import/xlsx", formData);
  }
  async getEngineerCapacity(teamId, month) {
    return this.client.get(`/teams/${teamId}/engineer-capacity`, {
      params: { month },
    });
  }
  async saveEngineerCapacity(teamId, month, engineers) {
    return this.client.put(`/teams/${teamId}/engineer-capacity`, {
      month,
      engineers,
    });
  }
  async getEngineerCapacitySummary(teamId, month) {
    return this.client.get(`/teams/${teamId}/engineer-capacity/summary`, {
      params: { month },
    });
  }
  // Add these methods to your ApiService class in services/api.js
  // Place them before the closing brace of the class

  // ============= Credit Management =============

  async getCredits(params = {}) {
    return this.client.get("/credits", { params });
  }

  async getCreditByReg(companyReg) {
    return this.client.get(`/credits/${companyReg}`);
  }

  async getCreditStats() {
    return this.client.get("/credits/stats/summary");
  }

  async exportCredits() {
    return this.client.get("/credits/export", { responseType: "blob" });
  }

  async recalculateCredit(companyReg) {
    return this.client.put(`/credits/${companyReg}/recalculate`);
  }

  async importCredits(files) {
    const formData = new FormData();
    if (files.coreFile) formData.append("coreFile", files.coreFile);
    if (files.creditFile) formData.append("creditFile", files.creditFile);
    if (files.directorFile) formData.append("directorFile", files.directorFile);
    return this.client.post("/credits/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
    });
  }
}
const apiService = new ApiService();
export default apiService;
